/**
 * ScenarioStackerPage — Compound What-If Lab
 *
 * Toggle multiple life-change levers (sell Farm Avenue, downsize, relocate,
 * delay retirement, extra savings, reduce spend) and instantly see the
 * compound effect on Monte Carlo success probability, IHT, and net income.
 *
 * Debounced re-fetch (500ms) on every control change.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import {
  Layers, RefreshCw, TrendingUp, Shield, Target, ChevronDown, ChevronUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api, getPropertyPlans } from '@/lib/api'
import ScenariosTabs from '@/components/ui/ScenariosTabs'
import { usePlanningAssumptions } from '@/hooks/usePlanningAssumptions'
import ScenarioDisclaimerFooter from '@/components/ScenarioDisclaimerFooter'
import EditableNumberSlider from '@/components/ui/EditableNumberSlider'
import PropertyLeverPanel, {
  defaultLeverState,
  type PropertyLeverState,
} from '@/components/PropertyLeverPanel'
import type { PropertySalePlanEnriched } from '@/lib/types'

// ── Types ────────────────────────────────────────────────────────────────────

interface StackSide {
  starting_pot_gbp: number
  target_gross_gbp: number
  target_net_gbp: number
  jurisdiction: string
  retirement_age: number
  success_probability_pct: number
  median_final_gbp: number
  iht_2027_gbp: number
}

interface StackDelta {
  starting_pot_gbp: number
  success_probability_pp: number
  iht_saving_gbp: number
  headline: string
}

interface StackResponse {
  baseline: StackSide
  stacked: StackSide
  delta: StackDelta
  levers_applied: string[]
  assumptions: Record<string, unknown>
}

// ── Format helpers ────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtPct = (n: number) => `${n.toFixed(1)}%`

const fmtDelta = (n: number, prefix = '+') =>
  n >= 0 ? `${prefix}${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}` : n.toLocaleString('en-GB', { maximumFractionDigits: 0 })

// ── Colour helpers ────────────────────────────────────────────────────────────

function successBadge(pct: number): string {
  if (pct >= 80) return 'bg-emerald/20 text-emerald border border-emerald/40'
  if (pct >= 50) return 'bg-amber/20 text-amber border border-amber/40'
  return 'bg-rose/20 text-rose-light border border-rose/40'
}

function successText(pct: number): string {
  if (pct >= 80) return 'text-emerald-light'
  if (pct >= 50) return 'text-amber'
  return 'text-rose-light'
}

function deltaBadge(n: number): string {
  if (n > 0) return 'text-emerald-light'
  if (n < 0) return 'text-rose-light'
  return 'text-text-muted'
}

// ── Slider component ──────────────────────────────────────────────────────────

function LeverSlider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  format: (v: number) => string
  onChange: (v: number) => void
}) {
  return (
    <div className="mt-2 pl-2 border-l border-border/40">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-text-muted">{label}</span>
        <span className="text-xs font-bold text-blue-light">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
      />
    </div>
  )
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function LeverToggle({
  id,
  label,
  sublabel,
  checked,
  onToggle,
  children,
}: {
  id: string
  label: string
  sublabel?: string
  checked: boolean
  onToggle: () => void
  children?: React.ReactNode
}) {
  return (
    <div className={clsx(
      'rounded-lg border p-3 transition-colors',
      checked ? 'border-blue/40 bg-blue/5' : 'border-border/30 bg-surface/40',
    )}>
      <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-0.5 w-4 h-4 accent-blue cursor-pointer"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-text-primary leading-tight">{label}</div>
          {sublabel && (
            <div className="text-[11px] text-text-muted mt-0.5 leading-tight">{sublabel}</div>
          )}
        </div>
      </label>
      {checked && children && <div className="mt-2">{children}</div>}
    </div>
  )
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  baseline,
  stacked,
  delta,
  format,
  higherIsBetter = true,
}: {
  label: string
  baseline: number
  stacked: number
  delta: number
  format: (v: number) => string
  higherIsBetter?: boolean
}) {
  const improved = higherIsBetter ? delta > 0 : delta < 0
  const unchanged = Math.abs(delta) < 0.05

  return (
    <div className="card p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div>
          <div className="text-[10px] text-text-muted mb-0.5">Baseline</div>
          <div className="text-base font-bold text-text-primary">{format(baseline)}</div>
        </div>
        <div>
          <div className="text-[10px] text-text-muted mb-0.5">Stacked</div>
          <div className={clsx('text-base font-bold', improved ? 'text-emerald-light' : unchanged ? 'text-text-primary' : 'text-rose-light')}>
            {format(stacked)}
          </div>
        </div>
      </div>
      <div>
        <span className={clsx(
          unchanged ? 'badge-neutral' : improved ? 'delta-chip-pos' : 'delta-chip-neg',
        )}>
          {delta >= 0 ? '+' : ''}{format(delta)}
        </span>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const JURISDICTIONS = ['UK', 'HK', 'SG', 'CY', 'MT', 'MO', 'PT'] as const
type Juri = typeof JURISDICTIONS[number]

const JURI_LABEL: Record<Juri, string> = {
  UK: 'United Kingdom',
  HK: 'Hong Kong',
  SG: 'Singapore',
  CY: 'Cyprus',
  MT: 'Malta',
  MO: 'Macau',
  PT: 'Portugal (NHR ref)',
}

export default function ScenarioStackerPage() {
  // ── Lever state ─────────────────────────────────────────────────────────────
  // NOTE: despite the backend param name `target_net_gbp`, this lever reads
  // and writes the canonical GROSS field `target_drawdown_gbp` — it is gross.
  const [targetGross, setTargetGross] = useState(220_000)
  const [retirementAge, setRetirementAge] = useState(60)

  // Phase C — generic per-property levers. Keyed by holding slug; value
  // captures whether the panel is active + the user's overrides.
  const [propertyPlans, setPropertyPlans] = useState<PropertySalePlanEnriched[]>([])
  const [propertyLevers, setPropertyLevers] = useState<Record<string, PropertyLeverState>>({})

  const [jurisdiction, setJurisdiction] = useState<Juri>('UK')

  const [delayYears, setDelayYears] = useState(0)
  const [addSavings, setAddSavings] = useState(0)
  const [reduceSpend, setReduceSpend] = useState(0)

  const [volatility, setVolatility] = useState(12)
  const [numSims, setNumSims] = useState(3_000)

  const [showAdvanced, setShowAdvanced] = useState(false)

  // Phase A — shared planning assumptions; bootstrap from resolver,
  // write through the store on slider changes.
  const assumptions = usePlanningAssumptions((s) => s.assumptions)
  const fetchAssumptions = usePlanningAssumptions((s) => s.fetch)
  const patchDebounced = usePlanningAssumptions((s) => s.patchDebounced)
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (!assumptions) {
      void fetchAssumptions()
      return
    }
    if (!hydratedRef.current) {
      hydratedRef.current = true
      setTargetGross(assumptions.target_drawdown_gbp ?? 220_000)
      setRetirementAge(assumptions.retirement_age)
      setJurisdiction((assumptions.planned_jurisdiction || 'UK') as Juri)
    }
  }, [assumptions, fetchAssumptions])

  // Phase C — fetch enriched property plans once and seed default lever
  // state per property. Re-run if the household switches.
  useEffect(() => {
    let cancelled = false
    void getPropertyPlans()
      .then((res) => {
        if (cancelled) return
        const plans = res.plans || []
        setPropertyPlans(plans)
        setPropertyLevers((prev) => {
          const next = { ...prev }
          for (const p of plans) {
            if (!next[p.holding_id]) next[p.holding_id] = defaultLeverState(p)
          }
          return next
        })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // ── Fetch state ──────────────────────────────────────────────────────────────
  const [data, setData] = useState<StackResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Debounced fetch ──────────────────────────────────────────────────────────
  const fetchStack = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Phase C — build generic property_sales list from active levers.
      const property_sales = Object.entries(propertyLevers)
        .filter(([, s]) => s.enabled)
        .map(([holding_id, s]) =>
          s.mode === 'sell'
            ? { holding_id, sale_price_gbp: s.sale_price_gbp }
            : { holding_id, downsize_to_gbp: s.downsize_to_gbp },
        )
      const res = await api.post<StackResponse>('/api/scenarios/stack', {
        target_net_gbp: targetGross,
        retirement_age: retirementAge,
        property_sales,
        jurisdiction,
        delay_retirement_years: delayYears,
        additional_savings_gbp_yr: addSavings,
        reduce_spend_pct: reduceSpend,
        volatility_pct: volatility,
        num_simulations: numSims,
      })
      setData(res.data)
    } catch (e: unknown) {
      setError((e as Error)?.message ?? 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [
    targetGross, retirementAge, propertyLevers, jurisdiction,
    delayYears, addSavings, reduceSpend, volatility, numSims,
  ])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(fetchStack, 500)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [fetchStack])

  const n = data
  const activeLevers = n?.levers_applied?.length ?? 0

  return (
    <div className="flex flex-col gap-6 pb-10">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers className="w-5 h-5 text-blue-light" />
            <h1 className="page-title">Scenario Stacker</h1>
            <span className="badge-neutral text-[10px] uppercase tracking-wider">
              Compound What-If Lab
            </span>
          </div>
          <p className="text-sm text-text-muted max-w-xl">
            Stack multiple life-change levers to see their combined effect on
            Monte Carlo success probability, IHT exposure, and net retirement income.
          </p>
        </div>
        <button
          onClick={fetchStack}
          disabled={loading}
          className="btn-secondary flex items-center gap-1.5 text-xs"
          aria-label="Re-run"
        >
          <RefreshCw className={clsx('w-3.5 h-3.5', loading && 'animate-spin')} />
          {loading ? 'Running…' : 'Re-run'}
        </button>
      </div>

      <ScenariosTabs />

      {error && (
        <div className="card p-3 border-rose/40 bg-rose/5 text-rose-light text-sm">{error}</div>
      )}

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">

        {/* ── LEFT: Lever panel ── */}
        <div className="flex flex-col gap-3">
          {/* Global targets */}
          <div className="card p-4">
            <h2 className="panel-title mb-3">
              Global targets
            </h2>
            <div className="flex flex-col gap-3">
              <EditableNumberSlider
                className="mt-2 pl-2 border-l border-border/40"
                label="Target gross income / yr"
                value={targetGross}
                min={50_000}
                max={500_000}
                step={5_000}
                format={(v) => fmt(v)}
                onChange={(v) => { setTargetGross(v); patchDebounced({ target_drawdown_gbp: v }) }}
              />
              <LeverSlider
                label="Base retirement age"
                value={retirementAge}
                min={55}
                max={70}
                step={1}
                format={(v) => `Age ${v}`}
                onChange={(v) => { setRetirementAge(v); patchDebounced({ retirement_age: v }) }}
              />
            </div>
          </div>

          {/* Phase C — one panel per active real-estate holding. Empty when
              the household has no properties tracked. */}
          {propertyPlans.length > 0 && (
            <div className="card p-4">
              <h2 className="panel-title mb-3">
                Property levers
              </h2>
              <div className="flex flex-col gap-2">
                {propertyPlans.map((plan) => {
                  const state = propertyLevers[plan.holding_id] ?? defaultLeverState(plan)
                  return (
                    <PropertyLeverPanel
                      key={plan.holding_id}
                      plan={plan}
                      state={state}
                      onChange={(next) =>
                        setPropertyLevers((prev) => ({ ...prev, [plan.holding_id]: next }))
                      }
                    />
                  )
                })}
              </div>
            </div>
          )}

          {/* Retirement levers */}
          <div className="card p-4">
            <h2 className="panel-title mb-3">
              Retirement & savings levers
            </h2>
            <div className="flex flex-col gap-3">
              <LeverSlider
                label="Delay retirement by (years)"
                value={delayYears}
                min={0}
                max={10}
                step={1}
                format={(v) => v === 0 ? 'No delay' : `+${v} yr${v > 1 ? 's' : ''}`}
                onChange={setDelayYears}
              />
              <LeverSlider
                label="Additional savings £/yr"
                value={addSavings}
                min={0}
                max={100_000}
                step={2_500}
                format={fmt}
                onChange={setAddSavings}
              />
              <LeverSlider
                label="Reduce spend by %"
                value={reduceSpend}
                min={0}
                max={50}
                step={5}
                format={(v) => `${v}%`}
                onChange={setReduceSpend}
              />
            </div>
          </div>

          {/* Jurisdiction */}
          <div className="card p-4">
            <h2 className="panel-title mb-3">
              Jurisdiction
            </h2>
            <select
              value={jurisdiction}
              onChange={(e) => { const v = e.target.value as Juri; setJurisdiction(v); patchDebounced({ planned_jurisdiction: v }) }}
              className="input"
            >
              {JURISDICTIONS.map((j) => (
                <option key={j} value={j}>{j} — {JURI_LABEL[j]}</option>
              ))}
            </select>
            {jurisdiction !== 'UK' && (
              <p className="text-[11px] text-amber mt-1.5">
                Non-UK jurisdiction: IHT = £0 (moves outside UK estate).
              </p>
            )}
          </div>

          {/* Advanced MC params */}
          <div className="card p-4">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center justify-between w-full panel-title"
            >
              <span>MC parameters</span>
              {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showAdvanced && (
              <div className="flex flex-col gap-3 mt-3">
                <LeverSlider
                  label="Volatility %"
                  value={volatility}
                  min={5}
                  max={25}
                  step={1}
                  format={(v) => `${v}%`}
                  onChange={setVolatility}
                />
                <LeverSlider
                  label="Simulations"
                  value={numSims}
                  min={500}
                  max={10_000}
                  step={500}
                  format={(v) => v.toLocaleString()}
                  onChange={setNumSims}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Results ── */}
        <div className="flex flex-col gap-4">
          {/* Loading skeleton */}
          {loading && !data && (
            <div className="space-y-3">
              <div className="h-20 skeleton" />
              <div className="h-44 skeleton" />
              <div className="h-44 skeleton" />
            </div>
          )}

          {data && (
            <>
              {/* ── Delta headline callout ── */}
              <div className={clsx(
                'card p-4 border-l-4',
                n!.delta.success_probability_pp >= 20
                  ? 'border-l-emerald bg-emerald/5'
                  : n!.delta.success_probability_pp >= 5
                  ? 'border-l-amber bg-amber/5'
                  : 'border-l-blue bg-blue/5',
              )}>
                <div className="flex items-start gap-3">
                  <TrendingUp className={clsx(
                    'w-5 h-5 mt-0.5 flex-shrink-0',
                    n!.delta.success_probability_pp >= 20 ? 'text-emerald-light' : 'text-blue-light',
                  )} />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        Combined delta
                      </span>
                      {activeLevers > 0 && (
                        <span className="text-[10px] bg-blue/20 text-blue-light px-1.5 py-0.5 rounded border border-blue/30">
                          {activeLevers} lever{activeLevers > 1 ? 's' : ''} active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-primary leading-relaxed">
                      {n!.delta.headline}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── KPI comparison grid ── */}
              <div className="grid grid-cols-2 gap-3 stagger-children">
                {/* Success probability */}
                <div className="card p-4 col-span-2 sm:col-span-1">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-3 flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5" /> Success probability
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] text-text-muted mb-1">Baseline (UK)</div>
                      <div className={clsx('text-2xl font-bold', successText(n!.baseline.success_probability_pct))}>
                        {fmtPct(n!.baseline.success_probability_pct)}
                      </div>
                      <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-semibold', successBadge(n!.baseline.success_probability_pct))}>
                        {n!.baseline.success_probability_pct >= 80 ? 'Safe' : n!.baseline.success_probability_pct >= 50 ? 'Borderline' : 'Fragile'}
                      </span>
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted mb-1">Stacked ({n!.stacked.jurisdiction})</div>
                      <div className={clsx('text-2xl font-bold', successText(n!.stacked.success_probability_pct))}>
                        {fmtPct(n!.stacked.success_probability_pct)}
                      </div>
                      <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-semibold', successBadge(n!.stacked.success_probability_pct))}>
                        {n!.stacked.success_probability_pct >= 80 ? 'Safe' : n!.stacked.success_probability_pct >= 50 ? 'Borderline' : 'Fragile'}
                      </span>
                    </div>
                  </div>
                  <div className={clsx('mt-3 text-xs font-semibold', deltaBadge(n!.delta.success_probability_pp))}>
                    {n!.delta.success_probability_pp >= 0 ? '+' : ''}{n!.delta.success_probability_pp.toFixed(1)} pp
                  </div>
                </div>

                {/* IHT */}
                <div className="card p-4 col-span-2 sm:col-span-1">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-3 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" /> IHT bill (Apr 2027)
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] text-text-muted mb-1">Baseline</div>
                      <div className="text-xl font-bold text-rose-light">{fmt(n!.baseline.iht_2027_gbp)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted mb-1">Stacked</div>
                      <div className={clsx('text-xl font-bold', n!.stacked.iht_2027_gbp === 0 ? 'text-emerald-light' : 'text-rose-light')}>
                        {fmt(n!.stacked.iht_2027_gbp)}
                      </div>
                    </div>
                  </div>
                  <div className={clsx('mt-3 text-xs font-semibold', deltaBadge(n!.delta.iht_saving_gbp))}>
                    {n!.delta.iht_saving_gbp > 0 && `Saved: ${fmt(n!.delta.iht_saving_gbp)}`}
                    {n!.delta.iht_saving_gbp <= 0 && `Delta: ${fmtDelta(n!.delta.iht_saving_gbp)}`}
                  </div>
                </div>

                {/* Starting pot */}
                <KpiCard
                  label="Starting pot"
                  baseline={n!.baseline.starting_pot_gbp}
                  stacked={n!.stacked.starting_pot_gbp}
                  delta={n!.delta.starting_pot_gbp}
                  format={fmt}
                />

                {/* Gross needed */}
                <KpiCard
                  label="Gross withdrawal needed"
                  baseline={n!.baseline.target_gross_gbp}
                  stacked={n!.stacked.target_gross_gbp}
                  delta={n!.stacked.target_gross_gbp - n!.baseline.target_gross_gbp}
                  format={fmt}
                  higherIsBetter={false}
                />

                {/* Median final */}
                <KpiCard
                  label="Median final pot (age 95)"
                  baseline={n!.baseline.median_final_gbp}
                  stacked={n!.stacked.median_final_gbp}
                  delta={n!.stacked.median_final_gbp - n!.baseline.median_final_gbp}
                  format={fmt}
                />

                {/* Net target */}
                <KpiCard
                  label="Net income target"
                  baseline={n!.baseline.target_net_gbp}
                  stacked={n!.stacked.target_net_gbp}
                  delta={n!.stacked.target_net_gbp - n!.baseline.target_net_gbp}
                  format={fmt}
                  higherIsBetter={true}
                />
              </div>

              {/* ── Levers applied ── */}
              {n!.levers_applied.length > 0 && (
                <div className="card p-4">
                  <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">
                    Levers applied
                  </h3>
                  <ol className="flex flex-col gap-1.5">
                    {n!.levers_applied.map((lever, i) => (
                      <li key={i} className="flex gap-2 text-sm text-text-primary">
                        <span className="text-blue-light font-bold flex-shrink-0">{i + 1}.</span>
                        <span>{lever}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* ── Retirement summary strip ── */}
              <div className="card p-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Retirement age</div>
                  <div className="text-base font-bold text-text-primary">
                    {n!.baseline.retirement_age}
                    {n!.stacked.retirement_age !== n!.baseline.retirement_age && (
                      <span className="text-emerald-light ml-1">→ {n!.stacked.retirement_age}</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Jurisdiction</div>
                  <div className="text-base font-bold text-text-primary">
                    UK {n!.stacked.jurisdiction !== 'UK' && (
                      <span className="text-blue-light">→ {n!.stacked.jurisdiction}</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Simulations</div>
                  <div className="text-base font-bold text-text-primary">
                    {n!.assumptions.num_simulations?.toLocaleString?.() ?? numSims.toLocaleString()}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Methodology footer ── */}
      <div className="card p-4 text-xs text-text-muted">
        <strong className="text-text-secondary">Methodology:</strong>{' '}
        Monte Carlo runs {numSims.toLocaleString()} stochastic paths drawing annual real returns from
        Normal(5%, {volatility}%) over ages 57–95. Spending is flat in real terms (returns are already post-inflation).
        Gross withdrawal is solved via bisection so the net-of-tax household income matches your target
        (joint couple, income split equally). IHT modelled under post-April-2027 UK rules (SIPP inside estate);
        £0 for non-UK jurisdictions. Farm Avenue net proceeds = sale price − 2% costs − £284k mortgage.
        The Gables equity release = current £1.55M value − new value − 3% transaction costs.
        All returns are real (post-inflation). Not financial advice — consult a qualified adviser.
      </div>
      <ScenarioDisclaimerFooter />
    </div>
  )
}
